import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AuditLog {
  id: string;
  org_id: string;
  entity_type: string;
  entity_id: string;
  action: string;
  actor_id: string | null;
  before: any;
  after: any;
  at: string;
  actor?: {
    display_name: string;
    email: string;
  };
}

interface UseAuditLogsParams {
  entityType?: string;
  entityId?: string;
  actorId?: string;
  startDate?: Date;
  endDate?: Date;
  limit?: number;
}

export function useAuditLogs(params: UseAuditLogsParams = {}) {
  const { entityType, entityId, actorId, startDate, endDate, limit = 100 } = params;

  return useQuery({
    queryKey: ["audit-logs", entityType, entityId, actorId, startDate, endDate, limit],
    queryFn: async () => {
      let query = supabase
        .from("audit_log")
        .select(`
          *,
          actor:actor_id (
            display_name,
            email
          )
        `)
        .order("at", { ascending: false })
        .limit(limit);

      if (entityType) {
        query = query.eq("entity_type", entityType);
      }

      if (entityId) {
        query = query.eq("entity_id", entityId);
      }

      if (actorId) {
        query = query.eq("actor_id", actorId);
      }

      if (startDate) {
        query = query.gte("at", startDate.toISOString());
      }

      if (endDate) {
        query = query.lte("at", endDate.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as AuditLog[];
    },
  });
}

// Get audit logs for a specific entity
export function useEntityAuditLogs(entityType: string, entityId: string) {
  return useAuditLogs({ entityType, entityId, limit: 50 });
}

// Get audit log statistics
export function useAuditLogStats() {
  return useQuery({
    queryKey: ["audit-log-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("audit_log")
        .select("action, entity_type", { count: "exact", head: false });

      if (error) throw error;

      // Count by action
      const actionCounts = data.reduce((acc: Record<string, number>, log) => {
        acc[log.action] = (acc[log.action] || 0) + 1;
        return acc;
      }, {});

      // Count by entity type
      const entityCounts = data.reduce((acc: Record<string, number>, log) => {
        acc[log.entity_type] = (acc[log.entity_type] || 0) + 1;
        return acc;
      }, {});

      return {
        totalLogs: data.length,
        actionCounts,
        entityCounts,
      };
    },
  });
}
